import React, { useEffect, useState, useCallback } from 'react'
import NumberFormat from 'react-number-format'
import MaskedInput from 'react-text-mask'
import { isBefore, format, isValid } from 'date-fns'

import { ReactComponent as TrashCan } from '../../assets/trash.svg'
import { lastDayOfMonth } from '../../utils/Dates.jsx'

import './PaymentBuilder.css'

export default function PaymentInput({
	handleRemovePaymentInput,
	index,
	definition,
	value,
	inputData,
	setInputData,
	trigger,
	setTrigger,
	startDate,
	endDate,
	setAdvance,
	updateValueTracker,
	checkValues,
	fairValue,
	setLoading,
	setShowPayments,
	jsonObjs,
	setJsonObjs,
}) {
	const [errorMessage, setErrorMessage] = useState('')

	const {
		paymentAmountTitle,
		paymentFrequencyTitle,
		firstPaymentDateTitle,
		numberOfPaymentsTitle,
		lastPaymentDateTitle,
		escalationTitle,
		escalationFrequencyTitle,
	} = definition

	let savedValue

	if (value[index]) {
		savedValue = value[index]
	} else if (!value[index] && inputData[index]) {
		savedValue = inputData[index]
	}

	const [paymentState, setPaymentState] = useState({
		paymentAmount: savedValue ? savedValue['paymentAmount'] : '',
		paymentFrequency: savedValue ? savedValue['paymentFrequency'] : 1,
		firstPaymentDate: savedValue ? savedValue['firstPaymentDate'] : '',
		numberOfPayments: savedValue ? savedValue['numberOfPayments'] : '',
		lastPaymentDate: savedValue ? savedValue['lastPaymentDate'] : '',
		escalation: savedValue ? savedValue['escalation'] : 'Percent',
		escalationAmount: savedValue ? savedValue['escalationAmount'] : '',
		escalationFrequency: savedValue ? savedValue['escalationFrequency'] : 1,
		escalationFrequencyAmount: savedValue
			? savedValue['escalationFrequencyAmount']
			: '',
	})

	function handleChange(e) {
		const value = e.target.value
		setPaymentState({
			...paymentState,
			[e.target.name]: value,
		})
	}

	const regexp = /^[0-9]*(\.[0-9]{0,2})?$/

	function handleChangeRegex(e) {
		let value = parseFloat(e.target.value.replace(/,/g, '')) || ''

		if (value === '' || regexp.test(value)) {
			setPaymentState({
				...paymentState,
				[e.target.name]: value,
			})
		}
	}

	const calculateEndDate = useCallback(() => {
		if (paymentState.firstPaymentDate && paymentState.numberOfPayments) {
			let numOfPayments = parseInt(paymentState.numberOfPayments) - 1
			let date = paymentState.firstPaymentDate

			if (parseInt(paymentState.paymentFrequency) === 1) {
				let newYear = parseInt(date.split('/')[2])
				let newMonth = parseInt(date.split('/')[0])
				let newDate = new Date(newYear, newMonth + numOfPayments, 0)

				let formatDate

				if (isValid(newDate)) {
					formatDate = format(newDate, 'MM/dd/yyyy')
				} else {
					formatDate = ''
				}

				return formatDate
			} else if (parseInt(paymentState.paymentFrequency) === 3) {
				let newYear = parseInt(date.split('/')[2])
				let newMonth = parseInt(date.split('/')[0])
				let newDate = new Date(newYear + numOfPayments, newMonth, 0)

				let formatDate

				if (isValid(newDate)) {
					formatDate = format(newDate, 'MM/dd/yyyy')
				} else {
					formatDate = ''
				}

				return formatDate
			}
		}
	}, [
		paymentState.firstPaymentDate,
		paymentState.numberOfPayments,
		paymentState.paymentFrequency,
	])

	let jsonObjPayments = {}

	jsonObjPayments = {
		leaseStartDate: startDate,
		leaseEndDate: endDate,
		paymentOptionType: 2,
		paymentDetail: [
			{
				paymentAmt: paymentState && paymentState.paymentAmount,
				paymentFrequency: paymentState && paymentState.paymentFrequency,
				firstPaymentDt: paymentState && paymentState.firstPaymentDate,
				lastPaymentDt:
					(paymentState && paymentState.lastPaymentDate) || calculateEndDate(),
				isIncrementPct:
					paymentState && paymentState.escalation === 'Percent' ? true : false,
				incrementDecrementAmt: paymentState && paymentState.escalationAmount,
				escalationNbrOfPayments:
					paymentState && paymentState.escalationFrequencyAmount,
				escalationFrequency: paymentState && paymentState.escalationFrequency,
			},
		],
	}

	const validate = useCallback(() => {
		const numberEndDate = calculateEndDate()

		if (paymentState.paymentAmount === '') {
			setErrorMessage('Payment Amount is required.')
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (Number(paymentState.paymentAmount) >= Number(fairValue)) {
			setErrorMessage(
				'Payment Amount cannot be greater than or equivalent to Fair Value of lease.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			paymentState.firstPaymentDate === '' ||
			(paymentState.firstPaymentDate !== '' &&
				paymentState.firstPaymentDate.slice(3, 5) !== '01' &&
				!lastDayOfMonth(paymentState.firstPaymentDate))
		) {
			setErrorMessage(
				'First Payment Date must be the beginning or end of the payment period.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			paymentState.lastPaymentDate === '' &&
			(paymentState.numberOfPayments === '' ||
				paymentState.numberOfPayments === 0)
		) {
			setErrorMessage('# of Payments or Last Payment Date is required.')
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			(paymentState.lastPaymentDate === '' &&
				paymentState.numberOfPayments === '') ||
			(paymentState.lastPaymentDate === '' &&
				paymentState.numberOfPayments === 0) ||
			(paymentState.lastPaymentDate !== '' &&
				paymentState.lastPaymentDate.slice(3, 5) !== '01' &&
				!lastDayOfMonth(paymentState.lastPaymentDate))
		) {
			setErrorMessage(
				'Last Payment Date must be the beginning or end of the payment period.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			isBefore(
				new Date(paymentState.lastPaymentDate),
				new Date(paymentState.firstPaymentDate)
			)
		) {
			setErrorMessage(
				'First Payment Date must occur before the Last Payment Date.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			isBefore(new Date(paymentState.firstPaymentDate), new Date(startDate))
		) {
			setErrorMessage(
				'First Payment Date should not occur before the Lease Start Date.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			(isValid(new Date(paymentState.lastPaymentDate)) &&
				isBefore(new Date(endDate), new Date(paymentState.lastPaymentDate))) ||
			(isValid(new Date(numberEndDate)) &&
				isBefore(new Date(endDate), new Date(numberEndDate)))
		) {
			setErrorMessage(
				'Last Payment Date should not be greater than the Lease End Date.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			(paymentState.escalationAmount > 0 &&
				paymentState.escalationFrequencyAmount === 0) ||
			(paymentState.escalationAmount > 0 &&
				paymentState.escalationFrequencyAmount === '')
		) {
			setErrorMessage('Please specify the Frequency of the Escalation.')
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			(paymentState.escalationFrequencyAmount > 0 &&
				paymentState.escalationAmount === 0) ||
			(paymentState.escalationFrequencyAmount > 0 &&
				paymentState.escalationAmount === '')
		) {
			setErrorMessage('Please specify the Amount of the Escalation.')
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (!isValid(new Date(paymentState.firstPaymentDate))) {
			setErrorMessage('Please enter a valid First Payment Date.')
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else if (
			!isValid(new Date(paymentState.lastPaymentDate)) &&
			!isValid(new Date(numberEndDate))
		) {
			setErrorMessage(
				'Please enter a valid Last Payment Date or # of Payments.'
			)
			updateValueTracker(index, false)
			checkValues()
			setTrigger(false)
			return
		} else {
			setErrorMessage('')
			inputData[index] = paymentState
			jsonObjs[index] = jsonObjPayments
			setInputData(inputData)
			setJsonObjs(jsonObjs)
			updateValueTracker(index, true)
			checkValues()
			setTrigger(false)
		}
	}, [
		calculateEndDate,
		paymentState,
		fairValue,
		startDate,
		endDate,
		updateValueTracker,
		index,
		checkValues,
		setTrigger,
		inputData,
		jsonObjs,
		jsonObjPayments,
		setInputData,
		setJsonObjs,
	])

	useEffect(() => {
		if (trigger) {
			validate()
		}
	}, [trigger, validate])

	useEffect(() => {
		inputData[index] = paymentState
		setInputData(inputData)
	}, [paymentState, index])

	useEffect(() => {
		let updatedPaymentState = inputData[index]
		setPaymentState(updatedPaymentState)
	}, [inputData, index])

	return (
		<>
			<div className='inner-content' style={{ marginBottom: '10px' }}>
				<div className='delete'>
					<span className='error-message'>{errorMessage}</span>
					<TrashCan onClick={() => handleRemovePaymentInput(index)} />
				</div>
				<div className='content-section'>
					<label>{paymentAmountTitle}</label>
					<NumberFormat
						min='0'
						name='paymentAmount'
						key={0}
						value={paymentState ? paymentState.paymentAmount : ''}
						onKeyDown={(e) =>
							(e.keyCode === 69 ||
								e.keyCode === 187 ||
								e.keyCode === 189 ||
								e.keyCode === 107 ||
								e.keyCode === 109) &&
							e.preventDefault()
						}
						onChange={(e) => {
							handleChangeRegex(e)
							setShowPayments(false)
							setAdvance(false)
							setLoading(true)
						}}
						thousandSeparator={true}
						isNumericString={true}
						required
					/>
				</div>
				<div className='content-section'>
					<label>{paymentFrequencyTitle}</label>
					<select
						name='paymentFrequency'
						key={1}
						value={paymentState ? paymentState.paymentFrequency : 1}
						onChange={(e) => {
							handleChange(e)
							setShowPayments(false)
							setAdvance(false)
							setLoading(true)
						}}
						required
					>
						<option value={1}>Monthly</option>
						<option value={3}>Annually</option>
					</select>
				</div>
				<div className='content-section'>
					<label>{firstPaymentDateTitle}</label>
					<MaskedInput
						className='firstPaymentDate'
						onChange={(e) => {
							handleChange(e)
							setShowPayments(false)
							setAdvance(false)
							setLoading(true)
						}}
						value={paymentState ? paymentState.firstPaymentDate : ''}
						placeholder='mm/dd/yyyy'
						mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
						placeholderChar={'\u2000'}
						key={2}
						name='firstPaymentDate'
					/>
				</div>
				<div className='gold-border'>
					<div className='content-section'>
						<label>{numberOfPaymentsTitle}</label>
						<NumberFormat
							min='0'
							name='numberOfPayments'
							key={3}
							value={paymentState ? paymentState.numberOfPayments : ''}
							onKeyDown={(e) =>
								(e.keyCode === 69 ||
									e.keyCode === 187 ||
									e.keyCode === 189 ||
									e.keyCode === 107 ||
									e.keyCode === 109) &&
								e.preventDefault()
							}
							onChange={(e) => {
								handleChangeRegex(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							thousandSeparator={true}
							isNumericString={true}
							disabled={
								paymentState && paymentState.lastPaymentDate ? 'disabled' : ''
							}
							required
						/>
					</div>
					<span className='display-none'>OR</span>
					<div className='content-section'>
						<label>{lastPaymentDateTitle}</label>
						<MaskedInput
							className='lastPaymentDate'
							value={paymentState ? paymentState.lastPaymentDate : ''}
							onChange={(e) => {
								handleChange(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							placeholder='mm/dd/yyyy'
							mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
							placeholderChar={'\u2000'}
							key={4}
							name='lastPaymentDate'
							disabled={
								paymentState && paymentState.numberOfPayments ? 'disabled' : ''
							}
						/>
					</div>
				</div>
				<div className='content-section'>
					<label>{escalationTitle}</label>
					<div className='multi-input'>
						<NumberFormat
							min='0'
							name='escalationAmount'
							value={
								paymentState && paymentState.escalationAmount > 0
									? paymentState.escalationAmount
									: ''
							}
							key={5}
							onKeyDown={(e) =>
								(e.keyCode === 69 ||
									e.keyCode === 187 ||
									e.keyCode === 189 ||
									e.keyCode === 107 ||
									e.keyCode === 109) &&
								e.preventDefault()
							}
							onChange={(e) => {
								handleChangeRegex(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							thousandSeparator={true}
							isNumericString={true}
							required
						/>
						<select
							key={6}
							name='escalation'
							value={paymentState ? paymentState.escalation : 'Percent'}
							onChange={(e) => {
								handleChange(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							required
						>
							<option value='Percent'>Percent</option>
							<option value='Amount'>Amount</option>
						</select>
					</div>
				</div>
				<div className='content-section'>
					<label>{escalationFrequencyTitle}</label>
					<div className='multi-input'>
						<NumberFormat
							min='0'
							name='escalationFrequencyAmount'
							value={
								paymentState && paymentState.escalationFrequencyAmount > 0
									? paymentState.escalationFrequencyAmount
									: ''
							}
							key={7}
							onKeyDown={(e) =>
								(e.keyCode === 69 ||
									e.keyCode === 187 ||
									e.keyCode === 189 ||
									e.keyCode === 107 ||
									e.keyCode === 109) &&
								e.preventDefault()
							}
							onChange={(e) => {
								handleChangeRegex(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							thousandSeparator={true}
							isNumericString={true}
							required
						/>
						<select
							name='escalationFrequency'
							key={8}
							value={paymentState ? paymentState.escalationFrequency : 1}
							onChange={(e) => {
								handleChange(e)
								setShowPayments(false)
								setAdvance(false)
								setLoading(true)
							}}
							required
						>
							<option value={1}>Month(s)</option>
							<option value={2}>Quarter(s)</option>
							<option value={3}>Year(s)</option>
						</select>
					</div>
				</div>
			</div>
		</>
	)
}
